import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import '../styles/Wallet.css';
import { fetchWithAuth } from '../utils/api';

function Wallet() {
  const { user, session } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [activeTab, setActiveTab] = useState('earnings');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchData = useCallback(async () => {
    // Ensure we have both user and a valid session with access token
    if (!user?.id || !session?.access_token) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all([
        // Fetch wallet
        fetchWithAuth(`${API_BASE_URL}/connect/wallet/${user.id}`, {}, session)
          .then(res => {
            if (!res.ok) throw new Error(`Wallet fetch failed: ${res.status}`);
            return res.json();
          })
          .then(data => { if (data.wallet) setWallet(data.wallet); }),
        // Fetch connect status
        fetchWithAuth(`${API_BASE_URL}/connect/account/${user.id}/status`, {}, session)
          .then(res => {
            if (!res.ok) throw new Error(`Status fetch failed: ${res.status}`);
            return res.json();
          })
          .then(data => { if (data) setConnectStatus(data); }),
        // Fetch earnings
        fetchWithAuth(`${API_BASE_URL}/connect/earnings/${user.id}`, {}, session)
          .then(res => {
            if (!res.ok) throw new Error(`Earnings fetch failed: ${res.status}`);
            return res.json();
          })
          .then(data => { setEarnings(data.earnings || []); }),
        // Fetch payouts
        fetchWithAuth(`${API_BASE_URL}/connect/payouts/${user.id}`, {}, session)
          .then(res => {
            if (!res.ok) throw new Error(`Payouts fetch failed: ${res.status}`);
            return res.json();
          })
          .then(data => { setPayouts(data.payouts || []); })
      ]);
      return results;
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      // Only set error if it's an auth issue (401/403)
      if (err.message.includes('401') || err.message.includes('403')) {
        setError('Authentication error. Please try signing out and back in.');
      }
    } finally {
      setLoading(false);
    }
  }, [user, session]);

  useEffect(() => {
    // Only fetch when we have both user and a valid session
    if (user && session?.access_token) {
      fetchData();
    }
  }, [user, session, fetchData]);

  const handleConnectStripe = async () => {
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/connect/account`, {
        method: 'POST',
        body: JSON.stringify({ user_id: user.id })
      }, session);
      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to start Stripe Connect setup');
      }
    } catch (err) {
      setError('Failed to connect to Stripe');
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/connect/account/${user.id}/dashboard-link`, {}, session);
      const data = await response.json();

      if (response.ok && data.url) {
        window.open(data.url, '_blank');
      } else {
        setError(data.error || 'Failed to open dashboard');
      }
    } catch (err) {
      setError('Failed to open Stripe dashboard');
    }
  };

  const handleWithdraw = async () => {
    setError(null);
    setSuccess(null);

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 1) {
      setError('Minimum withdrawal amount is $1.00');
      return;
    }

    if (amount > wallet?.available_balance) {
      setError('Insufficient balance');
      return;
    }

    setWithdrawing(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/connect/payout`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          amount: amount
        })
      }, session);
      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`Successfully withdrew $${amount.toFixed(2)}`);
        setWithdrawAmount('');
        fetchData();
      } else {
        setError(data.error || 'Failed to process withdrawal');
      }
    } catch (err) {
      setError('Failed to process withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  if (!user) {
    return (
      <div className="wallet-container">
        <h1>My Wallet</h1>
        <div className="wallet-sign-in-prompt">
          <p>Please sign in to view your wallet and manage your earnings.</p>
          <a href="/settings" className="sign-in-link">Go to Settings to Sign In</a>
        </div>
      </div>
    );
  }

  // Show loading while waiting for session to be ready or data to load
  if (loading || (user && !session?.access_token)) {
    return <div className="wallet-loading">Loading wallet...</div>;
  }

  return (
    <div className="wallet-container">
      <h1>My Wallet</h1>

      {error && <div className="wallet-error">{error}</div>}
      {success && <div className="wallet-success">{success}</div>}

      {/* Balance Overview */}
      <div className="balance-cards">
        <div className="balance-card available">
          <div className="balance-label">Available Balance</div>
          <div className="balance-amount">${wallet?.available_balance?.toFixed(2) || '0.00'}</div>
        </div>
        <div className="balance-card pending">
          <div className="balance-label">Pending</div>
          <div className="balance-amount">${wallet?.pending_balance?.toFixed(2) || '0.00'}</div>
        </div>
        <div className="balance-card total">
          <div className="balance-label">Total Earned</div>
          <div className="balance-amount">${wallet?.total_earned?.toFixed(2) || '0.00'}</div>
        </div>
        <div className="balance-card withdrawn">
          <div className="balance-label">Total Withdrawn</div>
          <div className="balance-amount">${wallet?.total_withdrawn?.toFixed(2) || '0.00'}</div>
        </div>
      </div>

      {/* Stripe Connect Section */}
      <div className="connect-section">
        <h2>Bank Account</h2>
        {!connectStatus?.connected ? (
          <div className="connect-prompt">
            <p>Connect your bank account to withdraw your earnings</p>
            <button onClick={handleConnectStripe} className="connect-button">
              Connect with Stripe
            </button>
          </div>
        ) : connectStatus.status === 'active' ? (
          <div className="connect-active">
            <div className="connect-status">
              <span className="status-indicator active"></span>
              Bank Account Connected
            </div>
            <button onClick={handleOpenDashboard} className="dashboard-button">
              View Stripe Dashboard
            </button>
          </div>
        ) : (
          <div className="connect-pending">
            <div className="connect-status">
              <span className="status-indicator pending"></span>
              Setup Incomplete
            </div>
            <p>Please complete your Stripe account setup to receive payouts</p>
            <button onClick={handleConnectStripe} className="connect-button">
              Complete Setup
            </button>
          </div>
        )}
      </div>

      {/* Withdraw Section */}
      {connectStatus?.status === 'active' && wallet?.available_balance > 0 && (
        <div className="withdraw-section">
          <h2>Withdraw Funds</h2>
          <div className="withdraw-form">
            <div className="withdraw-input-group">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="0.00"
                min="1"
                max={wallet?.available_balance}
                step="0.01"
              />
            </div>
            <button
              onClick={handleWithdraw}
              disabled={withdrawing || !withdrawAmount}
              className="withdraw-button"
            >
              {withdrawing ? 'Processing...' : 'Withdraw'}
            </button>
          </div>
          <p className="withdraw-note">
            Minimum withdrawal: $1.00 • Available: ${wallet?.available_balance?.toFixed(2)}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="wallet-tabs">
        <button
          className={`tab ${activeTab === 'earnings' ? 'active' : ''}`}
          onClick={() => setActiveTab('earnings')}
        >
          Earnings History
        </button>
        <button
          className={`tab ${activeTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('payouts')}
        >
          Withdrawal History
        </button>
      </div>

      {/* Earnings List */}
      {activeTab === 'earnings' && (
        <div className="history-list">
          {earnings.length === 0 ? (
            <p className="empty-message">No earnings yet. Complete tasks to earn!</p>
          ) : (
            earnings.map((earning) => (
              <div key={earning.id} className="history-item">
                <div className="history-info">
                  <div className="history-title">{earning.description || earning.task?.title}</div>
                  <div className="history-date">
                    {new Date(earning.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="history-amount positive">
                  +${earning.amount.toFixed(2)}
                </div>
                <div className={`history-status ${earning.status}`}>
                  {earning.status}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Payouts List */}
      {activeTab === 'payouts' && (
        <div className="history-list">
          {payouts.length === 0 ? (
            <p className="empty-message">No withdrawals yet</p>
          ) : (
            payouts.map((payout) => (
              <div key={payout.id} className="history-item">
                <div className="history-info">
                  <div className="history-title">Withdrawal to Bank</div>
                  <div className="history-date">
                    {new Date(payout.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="history-amount negative">
                  -${payout.amount.toFixed(2)}
                </div>
                <div className={`history-status ${payout.status}`}>
                  {payout.status}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default Wallet;
